import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { BulkActionConfirmationDialog } from '../BulkActionConfirmationDialog';
import { useStatePtr } from '@pure-ptr/react';

const useStyles = makeStyles(theme => ({
    button: {
        padding: '0.25rem'
    },
}));

export default function DeleteConfirmation({ handleDelete, customText = undefined, tooltip, buttonColor = undefined, customIcon, iconSize = undefined, isAllowedToDelete = true, dependenciesLength = 0, pageName = undefined, isPending = false }: {
    handleDelete: (processAll?: boolean) => void,
    tooltip?: string,
    buttonColor?: "default" | "inherit" | "primary" | "secondary",
    customIcon?: JSX.Element,
    iconSize?: "small" | "medium",
    isAllowedToDelete?: boolean
    dependenciesLength?: number | null
    customText?: string
    pageName?: string
    isPending?: boolean
}) {
    const [open, setOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const classes = useStyles();
    const processAllPtr = useStatePtr(false);

    // Auto-close dialog after operation is complete
    const prevPending = React.useRef(isPending);
    React.useEffect(() => {
        if (prevPending.current && !isPending && bulkOpen) {
            setBulkOpen(false);
        }
        if (prevPending.current && !isPending && open) {
            setOpen(false);
        }
        prevPending.current = isPending;
    }, [isPending, bulkOpen, open]);

    // Get table state if pageName is provided
    const selectedIds = useSelector((state: any) => pageName ? state.pagesV2[pageName]?.selected || [] : []);
    const totalCount = useSelector((state: any) => pageName ? state.pages[pageName]?.page?.totalRecords || 0 : 0);
    const currentPageCount = useSelector((state: any) => pageName ? state.pages[pageName]?.list?.length || 0 : 0);

    const handleClose = () => {
        setOpen(false);
    };

    const handleBulkClose = () => {
        setBulkOpen(false);
    }

    const handleOpen = () => {
        if (pageName && isAllowedToDelete && selectedIds.length > 0) {
            processAllPtr.set(false);
            setBulkOpen(true);
        } else {
            setOpen(true);
        }
    };

    const handleConfirmDelete = () => {
        if (!isPending) {
            handleDelete(false);
            if (isPending === undefined) {
                handleClose();
            }
        }
    }

    const handleBulkConfirm = (processAll: boolean) => {
        handleDelete(processAll);
        if (isPending === undefined) {
            setBulkOpen(false);
        }
    }

    return (<>
        <IconButton
            color={buttonColor ?? "primary"}
            className={classes.button}
            aria-label="Delete"
            onClick={handleOpen}
            size={iconSize ?? "medium"}
            disabled={isPending}
        >
            <Tooltip title={tooltip ?? "Delete"} >
                {customIcon ?? <DeleteIcon sx={{
                    color: '#d9534f',
                }} />}
            </Tooltip>
        </IconButton>
        
        {/* Standard Delete Dialog */}
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>
                Delete
            </DialogTitle>
            <DialogContent>
                {!customText ? (
                    <DialogContentText>
                        {isAllowedToDelete
                            ? `Are you sure you want to delete ${dependenciesLength > 0
                                ? `, It has ${dependenciesLength} ${dependenciesLength === 1
                                    ? "dependency"
                                    : "dependencies"
                                }`
                                : ""
                            }?`
                            : "Please select records to delete"}
                    </DialogContentText>
                ) : (
                    <DialogContentText>{customText}</DialogContentText>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    disabled={!isAllowedToDelete || isPending}
                    onClick={handleConfirmDelete}
                    color="primary">
                    {isPending ? 'Deleting...' : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Bulk Action Confirmation Dialog */}
        {pageName && (
            <BulkActionConfirmationDialog
                open={bulkOpen}
                onClose={handleBulkClose}
                onConfirm={handleBulkConfirm}
                title={tooltip || "Delete"}
                actionLabel="Delete"
                selectedCount={selectedIds.length}
                totalCount={totalCount}
                currentPageCount={currentPageCount}
                processAllPtr={processAllPtr}
                customText={customText}
                isPending={isPending}
            />
        )}
    </>);
}
